import {
  extractTickets,
  getRoomId,
  isResolved,
} from "../../pages/comms/Support/ticketUtils";

/**
 * Pure helpers behind the global support-ticket watch set: merging the
 * "mine" + "unassigned" ticket fetches into one deduped union, and decoding
 * realtime broadcast notification payloads. Extracted from
 * RoomsUnreadProvider / CommsNotifyProvider so they can be unit-tested.
 */

/**
 * Merge the settled results of the two ticket list calls
 * (`{status:"open", mine:true}` and `{unassigned:true}`) into one deduped,
 * unresolved ticket union keyed by room id. The "mine" entry wins on
 * duplicates — it carries the richer, server-normalized assignee fields.
 *
 * Degrades per-call: one rejected call just shrinks the union. Throws only
 * when BOTH calls rejected, so the caller keeps its last-known state.
 *
 * @param {PromiseSettledResult<*>} mineRes
 * @param {PromiseSettledResult<*>} unassignedRes
 * @returns {{ tickets: Array<Object>, mineDegraded: boolean, unassignedDegraded: boolean }}
 */
export const mergeTicketLists = (mineRes, unassignedRes) => {
  if (mineRes.status === "rejected" && unassignedRes.status === "rejected") {
    throw mineRes.reason;
  }
  const byRoom = new Map();
  const add = (res) => {
    if (res.status !== "fulfilled") {
      return;
    }
    for (const t of extractTickets(res.value)) {
      if (isResolved(t)) {
        continue;
      }
      const id = getRoomId(t);
      if (!id) {
        continue;
      }
      const key = String(id);
      if (!byRoom.has(key)) {
        byRoom.set(key, t);
      }
    }
  };
  // "mine" first so its entry wins any duplicate room id.
  add(mineRes);
  add(unassignedRes);
  return {
    tickets: Array.from(byRoom.values()),
    mineDegraded: mineRes.status === "rejected",
    unassignedDegraded: unassignedRes.status === "rejected",
  };
};

/**
 * Decode a `type:"notification"` publication from the personal
 * `broadcast:{userId}` channel into the fields the notify layer needs.
 *
 * The realtime wire shape is not formally documented (the REST feed uses
 * `{event_type, room_id, payload}` at the event level), so this probes the
 * plausible nesting spots and never throws.
 *
 * @param {*} data - Raw publication data.
 * @returns {{ eventType: string|null, roomId: string|null, subject: string|null,
 *   clock: 'first'|'next'|null, senderId: string|null,
 *   senderName: string|null, eventId: string|null,
 *   payload: Object|null }|null}
 */
export const parseBroadcastNotification = (data) => {
  if (!data || typeof data !== "object") {
    return null;
  }
  const ev =
    (typeof data.event === "object" && data.event) ||
    (typeof data.payload === "object" && data.payload) ||
    (typeof data.data === "object" && data.data) ||
    data;
  const eventType =
    ev.event_type || ev.eventType || data.event_type || data.eventType || null;
  const roomId =
    ev.room_id ?? ev.roomId ?? data.room_id ?? ev.payload?.room_id ?? null;
  const subject =
    ev.subject || ev.payload?.subject || ev.title || ev.payload?.title || null;
  // v1.9 SLA_BREACH says which of the two clocks blew, so the alert can name it.
  const clock = ev.payload?.clock ?? ev.clock ?? data.payload?.clock ?? null;
  // Who acted (MENTION carries the mentioning coach) — same probe-don't-throw
  // treatment; either or both may be absent depending on the event.
  const senderId = ev.sender_id ?? ev.payload?.sender_id ?? data.sender_id ?? null;
  const senderName =
    ev.sender_name || ev.payload?.sender_name || data.sender_name || null;
  // The event's own payload (lead alerts carry `{lead_id, lead_name, kind,
  // ...}` there). When `data.payload` was picked as `ev` above, it IS the
  // payload.
  const payload =
    (typeof ev.payload === "object" && ev.payload) ||
    (ev === data.payload ? data.payload : null);
  const eventId = data.event_id ?? ev.event_id ?? data.id ?? null;
  return {
    eventType: typeof eventType === "string" ? eventType : null,
    roomId: roomId != null ? String(roomId) : null,
    subject: typeof subject === "string" ? subject : null,
    clock: clock === "first" || clock === "next" ? clock : null,
    senderId: senderId != null ? String(senderId) : null,
    senderName: typeof senderName === "string" ? senderName : null,
    eventId: eventId != null ? String(eventId) : null,
    payload,
  };
};
